import { useMemo } from 'react';
import { createCachedSelector } from 're-reselect';
import { useQuery } from 'react-query';
import * as api from '../../services/api';
import useAccount from '../account/useAccount';
import { SORT_BY_STRING } from '../../utils/sorting';
import { QUERY_CACHE_TIME } from '../../constants';

// Shaped inside react-query's `select` (like useLocations) so the label work runs when the data
// changes, not on every consumer render.
const selector = createCachedSelector(
  (data) => data.data,
  (areas = []) => {
    // Areas of different locations can share a name, so prefix with the location when more than one
    // location is in play.
    const parentLocationIds = new Set(areas.map(({ parentLocationId }) => parentLocationId));
    const shouldPrefixLocation = parentLocationIds.size > 1;

    return {
      preferredAreasOptions: areas
        .map(({ id, name, parentLocationName }) => ({
          value: id,
          label: shouldPrefixLocation && parentLocationName ? `${parentLocationName} - ${name}` : name
        }))
        .sort(SORT_BY_STRING('label'))
    };
  }
)({
  keySelector: (data, storeKey) => storeKey.join('#')
});

// The "Preferred Areas" dropdown options: every area (sub-location) of the locations attached to
// the employee's branches. branchIds overrides the saved branches, so the profile form can list the
// areas of the branches currently selected in the form.
const useEmployeePreferredAreas = ({ employeeId, branchIds, enabled = true }) => {
  const { accountId } = useAccount();

  const sortedBranchIds = useMemo(() => [...(branchIds || [])].sort((a, b) => a - b), [branchIds]);
  const hasBranchIds = sortedBranchIds.length > 0;

  const storeKey = ['employeePreferredAreas', accountId, employeeId, sortedBranchIds];
  const { isLoading, data } = useQuery(
    storeKey,
    () => api.fetchEmployeePreferredAreas({ accountId, employeeId, branchIds: sortedBranchIds }),
    {
      select: (areasData) => selector(areasData, storeKey),
      cacheTime: QUERY_CACHE_TIME,
      // An empty branchIds array serializes to no param at all (qs drops empty arrays), which the
      // backend can't tell apart from "no branch filter" — it would then return areas across every
      // branch instead of none. So skip the request entirely and just report no options.
      enabled: enabled && !!accountId && !!employeeId && hasBranchIds
    }
  );

  const preferredAreasOptions = useMemo(() => (hasBranchIds ? data?.preferredAreasOptions || [] : []), [
    hasBranchIds,
    data?.preferredAreasOptions
  ]);

  return { isLoading: hasBranchIds && isLoading, preferredAreasOptions };
};

export default useEmployeePreferredAreas;
